import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatRelativeTime } from '../../lib/format';
import { threadLabel, useAssistantThreads } from './useAssistantThreads';

/** Rows the small menu shows; the full page lists the rest. */
const MENU_LIMIT = 12;

interface AssistantHistoryMenuProps {
  businessId: string;
  activeThreadId: string | null;
  onPick: (threadId: string) => void;
  onClose: () => void;
  /** "All chats" leaves for the full page, so the panel closes with it. */
  onOpenAll: () => void;
}

/**
 * The panel's thread switcher: a short list of recent chats and a way through
 * to the full page, where chats are renamed and deleted. A disclosure, not a
 * dialog: Escape and a click outside close it, and it is mounted only while
 * open, so the list is read when asked for.
 */
export function AssistantHistoryMenu({
  businessId,
  activeThreadId,
  onPick,
  onClose,
  onOpenAll,
}: AssistantHistoryMenuProps) {
  const { threads, isLoading, error } = useAssistantThreads(businessId);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const menu = menuRef.current;
      const target = event.target;
      if (!menu || !(target instanceof Node) || menu.contains(target)) return;
      // The toggle button handles its own click.
      if (target instanceof Element && target.closest('[data-assistant-history-toggle]')) return;
      closeRef.current();
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  return (
    <div
      className="assistant-history"
      ref={menuRef}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        // Escape closes the menu, not the whole panel.
        event.stopPropagation();
        onClose();
      }}
    >
      <p className="assistant-history__title">Recent chats</p>
      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}
      {isLoading ? (
        <p className="assistant-history__note" role="status">
          Loading your chats
        </p>
      ) : threads.length === 0 ? (
        <p className="assistant-history__note">No chats yet.</p>
      ) : (
        <ul className="assistant-history__list">
          {threads.slice(0, MENU_LIMIT).map((thread) => {
            const isActive = thread.id === activeThreadId;
            return (
              <li key={thread.id}>
                <button
                  className={
                    isActive
                      ? 'assistant-history__item assistant-history__item--active'
                      : 'assistant-history__item'
                  }
                  type="button"
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => onPick(thread.id)}
                >
                  <span className="assistant-history__label">{threadLabel(thread)}</span>
                  <span className="assistant-history__time">
                    {formatRelativeTime(thread.updated_at)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Link className="assistant-history__all" to="/assistant" onClick={onOpenAll}>
        All chats
      </Link>
    </div>
  );
}
